/**
 * @fileoverview Componente responsável por iniciar a criação de novos tickets.
 */

import React, { useContext, useEffect, useMemo, useRef, useState } from "react";

import {
  Avatar,
  Button,
  ButtonBase,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  InputBase,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Popover,
  Tooltip,
  Typography,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import KeyboardArrowRightIcon from "@material-ui/icons/KeyboardArrowRight";
import { Eye, Plus, UserPlus, Check, Search, X, ArrowLeft, ChevronDown } from "lucide-react";
import { toast } from "react-toastify";

import ContactModal from "../ContactModal";
import TicketMessagesDialog from "../TicketMessagesDialog";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import { i18n } from "../../translate/i18n";
import { AuthContext } from "../../context/Auth/AuthContext";
import { useQueues } from "../../context/Queues/QueuesContext";

const MIN_SEARCH_LENGTH = 3;
const MODAL_FONT_FAMILY = '"Montserrat", "Roboto", "Helvetica", "Arial", sans-serif';

const useStyles = makeStyles((theme) => ({
  dialog: {
    "& .MuiDialog-paper": {
      fontFamily: MODAL_FONT_FAMILY,
      width: "calc(100vw - 32px)",
      maxWidth: 1120,
      borderRadius: 24,
      overflow: "hidden",
      background:
        theme.palette.type === "dark"
          ? "linear-gradient(180deg, #171a20 0%, #111319 100%)"
          : "#ffffff",
      boxShadow:
        theme.palette.type === "dark"
          ? "0 24px 80px rgba(0, 0, 0, 0.45)"
          : "0 24px 60px rgba(15, 23, 42, 0.12)",
      border:
        theme.palette.type === "dark"
          ? "1px solid rgba(255, 255, 255, 0.08)"
          : "1px solid rgba(15, 23, 42, 0.08)",
      "& *": {
        fontFamily: MODAL_FONT_FAMILY,
      },
    },
  },
  compactDialogPaper: {
    width: "min(560px, calc(100vw - 32px)) !important",
    maxWidth: "calc(100vw - 32px) !important",
  },
  dialogTitle: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: theme.spacing(2.5, 3),
    borderBottom:
      theme.palette.type === "dark"
        ? "1px solid rgba(255, 255, 255, 0.06)"
        : "1px solid rgba(15, 23, 42, 0.08)",
  },
  dialogHeaderLead: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    minWidth: 0,
  },
  titleText: {
    fontSize: "1.35rem",
    fontWeight: 700,
    color: theme.palette.type === "dark" ? "#f8fafc" : "#111827",
  },
  backButton: {
    color: theme.palette.type === "dark" ? "rgba(248, 250, 252, 0.82)" : "#111827",
  },
  closeButton: {
    color: theme.palette.type === "dark" ? "rgba(248, 250, 252, 0.72)" : "#6b7280",
  },
  dialogContent: {
    padding: theme.spacing(3),
    background:
      theme.palette.type === "dark"
        ? "linear-gradient(180deg, rgba(255,255,255,0.01) 0%, rgba(255,255,255,0) 100%)"
        : "#ffffff",
  },
  contactView: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(2.5),
    width: "100%",
    minHeight: 520,
  },
  filtersRow: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(2),
    marginBottom: theme.spacing(3),
    [theme.breakpoints.down("sm")]: {
      flexWrap: "wrap",
      alignItems: "stretch",
    },
  },
  searchField: {
    flex: 1,
    minWidth: 280,
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1.5),
    padding: theme.spacing(0.7, 1.8),
    borderRadius: 16,
    border:
      theme.palette.type === "dark"
        ? "1px solid rgba(255, 255, 255, 0.12)"
        : "1px solid rgba(15, 23, 42, 0.12)",
    background:
      theme.palette.type === "dark"
        ? "rgba(255, 255, 255, 0.03)"
        : "rgba(248, 250, 252, 0.9)",
  },
  searchIcon: {
    color: theme.palette.type === "dark" ? "rgba(248, 250, 252, 0.72)" : "#6b7280",
  },
  searchInput: {
    flex: 1,
    color: theme.palette.type === "dark" ? "#f8fafc" : "#111827",
    fontSize: "0.98rem",
    "& input::placeholder": {
      color: theme.palette.type === "dark" ? "rgba(248, 250, 252, 0.55)" : "#6b7280",
      opacity: 1,
    },
  },
  tagsControl: {
    minWidth: 190,
  },
  tagsTrigger: {
    minWidth: 190,
    height: 48,
    borderRadius: 16,
    padding: theme.spacing(0, 1.5),
    justifyContent: "space-between",
    border:
      theme.palette.type === "dark"
        ? "1px solid rgba(255, 255, 255, 0.12)"
        : "1px solid rgba(15, 23, 42, 0.12)",
    background:
      theme.palette.type === "dark"
        ? "rgba(255, 255, 255, 0.03)"
        : "rgba(248, 250, 252, 0.9)",
  },
  tagsTriggerContent: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(0.8),
    minWidth: 0,
  },
  tagsTriggerLabel: {
    display: "block",
    fontSize: "0.98rem",
    color: theme.palette.type === "dark" ? "#f8fafc" : "#111827",
  },
  tagsCountBadge: {
    minWidth: 22,
    height: 22,
    padding: theme.spacing(0, 0.75),
    borderRadius: 999,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: "0.8rem",
    fontWeight: 700,
    lineHeight: 1,
    color: "#ffffff",
    background: "#1e80d7",
    boxShadow: "inset 0 0 0 1px rgba(255, 255, 255, 0.08)",
  },
  tagsTriggerIcon: {
    color: theme.palette.type === "dark" ? "rgba(248, 250, 252, 0.72)" : "#6b7280",
  },
  tagsPopover: {
    "& .MuiPopover-paper": {
      fontFamily: MODAL_FONT_FAMILY,
      width: 320,
      maxWidth: "calc(100vw - 32px)",
      maxHeight: "calc(100vh - 48px)",
      display: "flex",
      flexDirection: "column",
      marginTop: theme.spacing(1),
      borderRadius: 20,
      overflow: "hidden",
      background:
        theme.palette.type === "dark"
          ? "#22262b"
          : "#ffffff",
      color: theme.palette.type === "dark" ? "#f8fafc" : "#111827",
      border:
        theme.palette.type === "dark"
          ? "1px solid rgba(255, 255, 255, 0.08)"
          : "1px solid rgba(15, 23, 42, 0.08)",
      boxShadow:
        theme.palette.type === "dark"
          ? "0 20px 48px rgba(0, 0, 0, 0.45)"
          : "0 20px 44px rgba(15, 23, 42, 0.16)",
      "& *": {
        fontFamily: MODAL_FONT_FAMILY,
      },
    },
  },
  tagsPopoverHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: theme.spacing(1.75, 2, 1),
  },
  tagsPopoverTitle: {
    fontSize: "1rem",
    fontWeight: 700,
  },
  tagsPopoverClose: {
    color: theme.palette.type === "dark" ? "rgba(248, 250, 252, 0.72)" : "#6b7280",
  },
  tagsPopoverBody: {
    padding: theme.spacing(0, 1.5, 1.5),
    display: "flex",
    flexDirection: "column",
    minHeight: 0,
  },
  tagsSearchField: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    padding: theme.spacing(0.7, 1.4),
    borderRadius: 16,
    border:
      theme.palette.type === "dark"
        ? "1px solid rgba(255, 255, 255, 0.12)"
        : "1px solid rgba(15, 23, 42, 0.12)",
    background:
      theme.palette.type === "dark"
        ? "rgba(255, 255, 255, 0.03)"
        : "rgba(248, 250, 252, 0.9)",
  },
  tagsSearchInput: {
    flex: 1,
    color: theme.palette.type === "dark" ? "#f8fafc" : "#111827",
    "& input::placeholder": {
      color: theme.palette.type === "dark" ? "rgba(248, 250, 252, 0.55)" : "#6b7280",
      opacity: 1,
    },
  },
  tagsStatusRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: theme.spacing(1.25, 0.5, 1.25),
  },
  tagsStatusText: {
    fontSize: "0.95rem",
    color: theme.palette.type === "dark" ? "rgba(248, 250, 252, 0.76)" : "#6b7280",
  },
  tagsClearButton: {
    border: "none",
    background: "transparent",
    padding: 0,
    cursor: "pointer",
    fontSize: "0.95rem",
    color: theme.palette.primary.main,
  },
  tagsDivider: {
    marginBottom: theme.spacing(0.75),
  },
  tagsOptionsScrollArea: {
    maxHeight: 320,
    minHeight: 0,
    overflowY: "auto",
    paddingRight: theme.spacing(0.5),
    marginRight: theme.spacing(-0.5),
  },
  tagOption: {
    width: "100%",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: theme.spacing(1.1, 1.4),
    borderRadius: 14,
    marginBottom: theme.spacing(0.75),
    textAlign: "left",
    background: "transparent",
  },
  tagOptionSelected: {
    background:
      theme.palette.type === "dark"
        ? "rgba(255, 255, 255, 0.16)"
        : "rgba(59, 130, 246, 0.14)",
  },
  tagOptionLabel: {
    fontSize: "1rem",
    color: theme.palette.type === "dark" ? "#f8fafc" : "#111827",
  },
  tagOptionChip: {
    padding: theme.spacing(0.25, 1),
    borderRadius: 999,
    fontSize: "0.86rem",
    lineHeight: 1.4,
    color: "#f8fafc",
  },
  tagOptionCheck: {
    marginLeft: theme.spacing(1.5),
    fontSize: "1rem",
    color: theme.palette.type === "dark" ? "#7aa2ff" : theme.palette.primary.main,
  },
  tagEmptyState: {
    padding: theme.spacing(1.5, 0.5, 0.5),
    fontSize: "0.94rem",
    color: theme.palette.type === "dark" ? "rgba(248, 250, 252, 0.6)" : "#6b7280",
  },
  selectPlaceholder: {
    color: theme.palette.type === "dark" ? "rgba(248, 250, 252, 0.55)" : "#6b7280",
  },
  newContactButton: {
    height: 36,
    minWidth: 0,
    padding: "0 14px",
    borderRadius: 10,
    textTransform: "none",
    fontSize: "0.875rem",
    fontWeight: 500,
    boxShadow: "none",
    background: "linear-gradient(135deg, #4f7cff 0%, #3b82f6 100%)",
    color: "#ffffff",
    "&:hover": {
      boxShadow: "none",
      background: "linear-gradient(135deg, #3f6cf0 0%, #2563eb 100%)",
    },
    "& .MuiButton-startIcon": {
      marginRight: 6,
    },
  },
  actionsGroup: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1.5),
    marginLeft: "auto",
    [theme.breakpoints.down("sm")]: {
      width: "100%",
      marginLeft: 0,
    },
  },
  createTicketButton: {
    height: 36,
    minWidth: 0,
    padding: "0 14px",
    borderRadius: 10,
    textTransform: "none",
    fontSize: "0.875rem",
    fontWeight: 500,
    boxShadow: "none",
    color: theme.palette.type === "dark" ? "#f8fafc" : "#111827",
    border:
      theme.palette.type === "dark"
        ? "1px solid rgba(255, 255, 255, 0.12)"
        : "1px solid rgba(15, 23, 42, 0.12)",
    background:
      theme.palette.type === "dark"
        ? "rgba(255, 255, 255, 0.03)"
        : "rgba(248, 250, 252, 0.9)",
    "&:hover": {
      boxShadow: "none",
      background:
        theme.palette.type === "dark"
          ? "rgba(255, 255, 255, 0.06)"
          : "rgba(241, 245, 249, 1)",
    },
    "& .MuiButton-startIcon": {
      marginRight: 6,
    },
    [theme.breakpoints.down("sm")]: {
      flex: 1,
    },
  },
  createTicketView: {
    display: "flex",
    flexDirection: "column",
    width: "100%",
    minHeight: 520,
  },
  compactCreateTicketView: {
    minHeight: "auto",
  },
  createTicketFields: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(2),
    marginBottom: theme.spacing(3),
  },
  createTicketPrimaryRow: {
    display: "flex",
    alignItems: "stretch",
    gap: theme.spacing(2),
    [theme.breakpoints.down("sm")]: {
      flexWrap: "wrap",
      alignItems: "stretch",
    },
  },
  createTicketStackedFields: {
    flexDirection: "column",
    alignItems: "stretch",
    gap: theme.spacing(1.5),
  },
  createStackedSelectControl: {
    flex: "1 1 100% !important",
    minWidth: 0,
    width: "100% !important",
    maxWidth: "100%",
  },
  createSearchField: {
    flex: 1,
    minWidth: 280,
  },
  createQueueControl: {
    display: "flex",
    flex: "0 0 220px",
    minWidth: 220,
    [theme.breakpoints.down("sm")]: {
      flexBasis: "100%",
      width: "100%",
    },
  },
  createConnectionControl: {
    display: "flex",
    flex: "0 0 220px",
    minWidth: 220,
    width: 220,
    [theme.breakpoints.down("sm")]: {
      flexBasis: "100%",
      width: "100%",
    },
  },
  createSelectTrigger: {
    width: "100%",
    minWidth: 0,
    padding: theme.spacing(0, 1.8),
  },
  createSelectTriggerContent: {
    display: "flex",
    alignItems: "center",
    minWidth: 0,
    flex: 1,
  },
  createSelectTriggerLabel: {
    width: "100%",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    textAlign: "left",
  },
  createResultsContainer: {
    minHeight: 280,
    maxHeight: 360,
    overflowY: "auto",
    borderRadius: 18,
    border:
      theme.palette.type === "dark"
        ? "1px solid rgba(255, 255, 255, 0.06)"
        : "1px solid rgba(15, 23, 42, 0.08)",
    background:
      theme.palette.type === "dark"
        ? "rgba(255, 255, 255, 0.02)"
        : "linear-gradient(180deg, #ffffff 0%, #fbfdff 100%)",
  },
  createEmptyState: {
    minHeight: 280,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: theme.spacing(3),
    textAlign: "center",
    color: theme.palette.type === "dark" ? "rgba(248, 250, 252, 0.72)" : "#6b7280",
  },
  contactResultItemSelected: {
    backgroundColor:
      theme.palette.type === "dark"
        ? "rgba(255, 255, 255, 0.08)"
        : "rgba(59, 130, 246, 0.08)",
  },
  contactResultCheck: {
    color: theme.palette.type === "dark" ? "#7aa2ff" : theme.palette.primary.main,
  },
  createActions: {
    display: "flex",
    justifyContent: "flex-end",
    paddingTop: theme.spacing(2.5),
    marginTop: theme.spacing(2),
    borderTop:
      theme.palette.type === "dark"
        ? "1px solid rgba(255, 255, 255, 0.06)"
        : "1px solid rgba(15, 23, 42, 0.08)",
  },
  listContainer: {
    minHeight: 360,
    maxHeight: 420,
    overflowY: "auto",
    borderRadius: 18,
    border:
      theme.palette.type === "dark"
        ? "1px solid rgba(255, 255, 255, 0.06)"
        : "1px solid rgba(15, 23, 42, 0.08)",
    background:
      theme.palette.type === "dark"
        ? "rgba(255, 255, 255, 0.02)"
        : "linear-gradient(180deg, #ffffff 0%, #fbfdff 100%)",
  },
  sectionHeader: {
    padding: theme.spacing(1.5, 2.2, 1),
    fontSize: "0.78rem",
    fontWeight: 800,
    letterSpacing: "0.08em",
    textTransform: "uppercase",
    color: theme.palette.type === "dark" ? "rgba(248, 250, 252, 0.5)" : "#6b7280",
    background:
      theme.palette.type === "dark"
        ? "rgba(255, 255, 255, 0.01)"
        : "rgba(248, 250, 252, 0.9)",
  },
  emptyState: {
    minHeight: 360,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: theme.spacing(3),
    textAlign: "center",
    color: theme.palette.type === "dark" ? "rgba(248, 250, 252, 0.72)" : "#6b7280",
  },
  ticketItem: {
    padding: theme.spacing(1.8, 2.2),
    transition: "background-color 0.2s ease, transform 0.2s ease",
    "&:hover": {
      backgroundColor:
        theme.palette.type === "dark"
          ? "rgba(255, 255, 255, 0.04)"
          : "rgba(59, 130, 246, 0.04)",
    },
  },
  ticketAvatar: {
    width: 46,
    height: 46,
    background: "linear-gradient(135deg, #60a5fa 0%, #2563eb 100%)",
    color: "#ffffff",
    fontWeight: 700,
  },
  ticketName: {
    fontWeight: 700,
    color: theme.palette.type === "dark" ? "#f8fafc" : "#111827",
  },
  ticketPrimaryRow: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(0.75),
    flexWrap: "wrap",
  },
  ticketTagsRow: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(0.5),
    flexWrap: "wrap",
  },
  ticketTagChip: {
    display: "inline-flex",
    alignItems: "center",
    padding: theme.spacing(0.2, 0.9),
    borderRadius: 999,
    fontSize: "0.74rem",
    lineHeight: 1.4,
    fontWeight: 700,
    color: "#ffffff",
    textTransform: "uppercase",
  },
  ticketNumber: {
    marginTop: 2,
    color: theme.palette.type === "dark" ? "rgba(248, 250, 252, 0.68)" : "#6b7280",
  },
  ticketArrow: {
    color: theme.palette.type === "dark" ? "rgba(248, 250, 252, 0.35)" : "#9ca3af",
  },
  ticketActions: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(0.5),
    marginLeft: theme.spacing(1),
  },
  ticketPeekButton: {
    padding: theme.spacing(0.75),
    color: theme.palette.type === "dark" ? "rgba(248, 250, 252, 0.72)" : "#4b5563",
    "&:hover": {
      backgroundColor:
        theme.palette.type === "dark"
          ? "rgba(255, 255, 255, 0.06)"
          : "rgba(15, 23, 42, 0.06)",
    },
  },
  ticketPeekIcon: {
    fontSize: 19,
  },
}));

const formatPhoneNumber = (raw = "") => {
  const digits = String(raw || "").replace(/\D/g, "");

  if (!/^55\d{10,11}$/.test(digits)) {
    return raw || "";
  }

  const ddd = digits.slice(2, 4);
  const localNumber = digits.slice(4);
  const firstPart = localNumber.slice(0, localNumber.length - 4);
  const lastPart = localNumber.slice(-4);

  return `+55 (${ddd}) ${firstPart}-${lastPart}`;
};

const getColorWithOpacity = (color, opacity, fallback) => {
  if (!color || typeof color !== "string") {
    return fallback;
  }

  const normalizedColor = color.trim();

  if (/^#([0-9a-f]{3}){1,2}$/i.test(normalizedColor)) {
    let hex = normalizedColor.slice(1);

    if (hex.length === 3) {
      hex = hex
        .split("")
        .map((char) => char + char)
        .join("");
    }

    const red = parseInt(hex.slice(0, 2), 16);
    const green = parseInt(hex.slice(2, 4), 16);
    const blue = parseInt(hex.slice(4, 6), 16);

    return `rgba(${red}, ${green}, ${blue}, ${opacity})`;
  }

  const rgbaMatch = normalizedColor.match(
    /^rgba?\((\d+),\s*(\d+),\s*(\d+)(?:,\s*[\d.]+)?\)$/i
  );

  if (rgbaMatch) {
    const [, red, green, blue] = rgbaMatch;
    return `rgba(${red}, ${green}, ${blue}, ${opacity})`;
  }

  return fallback;
};

const NewTicketModal = ({
  modalOpen,
  onClose,
  initialContact,
  initialView = "picker",
}) => {
  const classes = useStyles();
  const { user } = useContext(AuthContext);
  const { queues: allQueues } = useQueues();
  const wasModalOpenRef = useRef(false);
  const connectionsLoadedRef = useRef(false);

  const [searchTerm, setSearchTerm] = useState("");
  const [ticketsByStatus, setTicketsByStatus] = useState({
    open: [],
    pending: [],
    closed: [],
  });
  const [tags, setTags] = useState([]);
  const [selectedTagIds, setSelectedTagIds] = useState([]);
  const [withoutTags, setWithoutTags] = useState(false);
  const [loadingTickets, setLoadingTickets] = useState(false);
  const [loadingTags, setLoadingTags] = useState(false);
  const [activeView, setActiveView] = useState("picker");
  const [tagsAnchorEl, setTagsAnchorEl] = useState(null);
  const [tagSearchTerm, setTagSearchTerm] = useState("");
  const [createContactSearchTerm, setCreateContactSearchTerm] = useState("");
  const [createContactResults, setCreateContactResults] = useState([]);
  const [selectedCreateContact, setSelectedCreateContact] = useState(null);
  const [loadingCreateContacts, setLoadingCreateContacts] = useState(false);
  const [selectedCreateQueueId, setSelectedCreateQueueId] = useState(null);
  const [createQueueAnchorEl, setCreateQueueAnchorEl] = useState(null);
  const [createQueueSearchTerm, setCreateQueueSearchTerm] = useState("");
  const [selectedTicketQueueFilterId, setSelectedTicketQueueFilterId] = useState(undefined);
  const [ticketQueueAnchorEl, setTicketQueueAnchorEl] = useState(null);
  const [ticketQueueSearchTerm, setTicketQueueSearchTerm] = useState("");
  const [connections, setConnections] = useState([]);
  const [loadingConnections, setLoadingConnections] = useState(false);
  const [selectedWhatsappId, setSelectedWhatsappId] = useState("");
  const [connectionsAnchorEl, setConnectionsAnchorEl] = useState(null);
  const [selectedTicketWhatsappFilterId, setSelectedTicketWhatsappFilterId] = useState("");
  const [ticketConnectionsAnchorEl, setTicketConnectionsAnchorEl] = useState(null);
  const [creatingTicket, setCreatingTicket] = useState(false);
  const [peekTicketId, setPeekTicketId] = useState(null);
  const [peekDialogOpen, setPeekDialogOpen] = useState(false);
  const isAdmin = user?.profile === "admin";

  const availableQueues = useMemo(
    () => (isAdmin ? allQueues || [] : user?.queues || []),
    [allQueues, isAdmin, user?.queues]
  );

  const availableQueueIds = useMemo(
    () => availableQueues.map((queue) => queue.id),
    [availableQueues]
  );

  const serializedQueueIds = useMemo(
    () => JSON.stringify(availableQueueIds),
    [availableQueueIds]
  );

  const serializedTagIds = useMemo(
    () => JSON.stringify(selectedTagIds),
    [selectedTagIds]
  );
  const tagsPopoverOpen = Boolean(tagsAnchorEl);
  const createQueuePopoverOpen = Boolean(createQueueAnchorEl);
  const connectionsPopoverOpen = Boolean(connectionsAnchorEl);
  const ticketQueuePopoverOpen = Boolean(ticketQueueAnchorEl);
  const ticketConnectionsPopoverOpen = Boolean(ticketConnectionsAnchorEl);
  const isContactTicketsView = activeView === "contactTickets";
  const isResolvingStartConversationView =
    activeView === "resolvingStartConversation";
  const isQuickCreateTicketView = activeView === "quickCreateTicket";
  const isCreateTicketView =
    activeView === "createTicket" || isQuickCreateTicketView;
  const shouldPrefillCreateContact =
    (initialView === "createTicket" || initialView === "quickCreateTicket") &&
    initialContact?.id;
  const isCompactCreateTicketView =
    isCreateTicketView && Boolean(shouldPrefillCreateContact);

  const trimmedSearch = searchTerm.trim();
  const trimmedTagSearch = tagSearchTerm.trim().toLowerCase();
  const trimmedCreateContactSearch = createContactSearchTerm.trim();
  const trimmedCreateQueueSearch = createQueueSearchTerm.trim().toLowerCase();
  const trimmedTicketQueueSearch = ticketQueueSearchTerm.trim().toLowerCase();

  const initializeModalState = (view) => {
    const shouldPrefillForView =
      (view === "createTicket" || view === "quickCreateTicket") &&
      Boolean(initialContact?.id);

    setTicketsByStatus({
      open: [],
      pending: [],
      closed: [],
    });
    setSelectedTagIds([]);
    setWithoutTags(false);
    setTagsAnchorEl(null);
    setTagSearchTerm("");
    setSearchTerm(initialContact?.number || initialContact?.name || "");
    setCreateContactSearchTerm(initialContact?.number || initialContact?.name || "");
    setCreateContactResults(shouldPrefillForView ? [initialContact] : []);
    setSelectedCreateContact(shouldPrefillForView ? initialContact : null);
    setLoadingTickets(false);
    setLoadingTags(false);
    setLoadingCreateContacts(false);
    setSelectedCreateQueueId(null);
    setCreateQueueAnchorEl(null);
    setCreateQueueSearchTerm("");
    setSelectedTicketQueueFilterId(undefined);
    setTicketQueueAnchorEl(null);
    setTicketQueueSearchTerm("");
    setConnections([]);
    setLoadingConnections(false);
    setSelectedWhatsappId("");
    setConnectionsAnchorEl(null);
    setSelectedTicketWhatsappFilterId("");
    setTicketConnectionsAnchorEl(null);
    setCreatingTicket(false);
    setPeekTicketId(null);
    setPeekDialogOpen(false);
    connectionsLoadedRef.current = false;
    setActiveView(view);
  };

  useEffect(() => {
    if (!modalOpen) {
      wasModalOpenRef.current = false;
      return;
    }

    if (wasModalOpenRef.current) {
      return;
    }

    wasModalOpenRef.current = true;
    initializeModalState(initialView);
  }, [modalOpen, initialContact, initialView, shouldPrefillCreateContact]);

  useEffect(() => {
    if (!modalOpen || !wasModalOpenRef.current) {
      return;
    }

    if (!isResolvingStartConversationView) {
      return;
    }

    if (initialView === "resolvingStartConversation") {
      return;
    }

    initializeModalState(initialView);
  }, [initialView, isResolvingStartConversationView, modalOpen]);

  useEffect(() => {
    if (!tagsPopoverOpen) {
      setTagSearchTerm("");
    }
  }, [tagsPopoverOpen]);

  useEffect(() => {
    if (!createQueuePopoverOpen) {
      setCreateQueueSearchTerm("");
    }
  }, [createQueuePopoverOpen]);

  useEffect(() => {
    if (!ticketQueuePopoverOpen) {
      setTicketQueueSearchTerm("");
    }
  }, [ticketQueuePopoverOpen]);

  useEffect(() => {
    if (!modalOpen || (activeView !== "picker" && !isContactTicketsView)) {
      return;
    }

    let isMounted = true;

    const loadTags = async () => {
      setLoadingTags(true);

      try {
        const { data } = await api.get("/tags/list");

        if (isMounted) {
          setTags(data || []);
        }
      } catch (err) {
        if (isMounted) {
          toastError(err);
        }
      } finally {
        if (isMounted) {
          setLoadingTags(false);
        }
      }
    };

    loadTags();

    return () => {
      isMounted = false;
    };
  }, [activeView, isContactTicketsView, modalOpen]);

  useEffect(() => {
    if (
      !modalOpen ||
      (!isCreateTicketView && activeView !== "contact" && !isContactTicketsView)
    ) {
      setLoadingConnections(false);
      return;
    }

    if (connectionsLoadedRef.current) {
      return;
    }

    let isMounted = true;

    const loadConnections = async () => {
      setLoadingConnections(true);

      try {
        const { data } = await api.get("/whatsapp");
        const activeConnections = Array.isArray(data)
          ? data.filter((connection) => connection.status === "CONNECTED")
          : [];

        if (isMounted) {
          setConnections(activeConnections);
          connectionsLoadedRef.current = true;

          setSelectedWhatsappId((currentValue) => {
            if (
              currentValue &&
              activeConnections.some(
                (connection) => String(connection.id) === String(currentValue)
              )
            ) {
              return currentValue;
            }

            if (activeConnections.length === 1) {
              return String(activeConnections[0].id);
            }

            return "";
          });
        }
      } catch (err) {
        if (isMounted) {
          setConnections([]);
          connectionsLoadedRef.current = false;
          toastError(err);
        }
      } finally {
        if (isMounted) {
          setLoadingConnections(false);
        }
      }
    };

    loadConnections();

    return () => {
      isMounted = false;
    };
  }, [activeView, isContactTicketsView, isCreateTicketView, modalOpen]);

  useEffect(() => {
    if (!modalOpen || activeView !== "picker") {
      setLoadingTickets(false);
      return;
    }

    if (trimmedSearch.length < MIN_SEARCH_LENGTH) {
      setLoadingTickets(false);
      setTicketsByStatus({
        open: [],
        pending: [],
        closed: [],
      });
      return;
    }

    let isMounted = true;
    setLoadingTickets(true);

    const delayDebounceFn = setTimeout(async () => {
      try {
        const { data } = await api.get("/tickets/picker-search", {
          params: {
            searchParam: trimmedSearch,
            tags: serializedTagIds,
            withoutTags,
            queueIds: serializedQueueIds,
            includeNullQueue: isAdmin ? "true" : undefined,
            showAll: isAdmin ? "true" : undefined,
          },
        });

        if (isMounted) {
          setTicketsByStatus({
            open: data?.ticketsByStatus?.open || [],
            pending: data?.ticketsByStatus?.pending || [],
            closed: data?.ticketsByStatus?.closed || [],
          });
        }
      } catch (err) {
        if (isMounted) {
          setTicketsByStatus({
            open: [],
            pending: [],
            closed: [],
          });
          toastError(err);
        }
      } finally {
        if (isMounted) {
          setLoadingTickets(false);
        }
      }
    }, 350);

    return () => {
      isMounted = false;
      clearTimeout(delayDebounceFn);
    };
  }, [
    activeView,
    isAdmin,
    modalOpen,
    trimmedSearch,
    serializedTagIds,
    serializedQueueIds,
    withoutTags,
  ]);

  const serializedTicketQueueFilterIds = useMemo(() => {
    if (selectedTicketQueueFilterId === undefined) {
      return JSON.stringify(availableQueueIds);
    }

    return JSON.stringify([selectedTicketQueueFilterId]);
  }, [availableQueueIds, selectedTicketQueueFilterId]);

  const serializedTicketWhatsappFilterIds = useMemo(() => {
    if (!selectedTicketWhatsappFilterId) {
      return undefined;
    }

    return JSON.stringify([Number(selectedTicketWhatsappFilterId)]);
  }, [selectedTicketWhatsappFilterId]);

  useEffect(() => {
    if (!modalOpen || !isContactTicketsView || !initialContact?.id) {
      return;
    }

    let isMounted = true;
    setLoadingTickets(true);

    const loadTickets = async () => {
      try {
        const { data } = await api.get("/tickets/picker-search", {
          params: {
            contactId: initialContact.id,
            tags: serializedTagIds,
            withoutTags,
            queueIds: serializedTicketQueueFilterIds,
            whatsappIds: serializedTicketWhatsappFilterIds,
            includeNullQueue: selectedTicketQueueFilterId === undefined ? "true" : "false",
            showAll: isAdmin ? "true" : undefined,
          },
        });

        if (isMounted) {
          setTicketsByStatus({
            open: data?.ticketsByStatus?.open || [],
            pending: data?.ticketsByStatus?.pending || [],
            closed: data?.ticketsByStatus?.closed || [],
          });
        }
      } catch (err) {
        if (isMounted) {
          setTicketsByStatus({
            open: [],
            pending: [],
            closed: [],
          });
          toastError(err);
        }
      } finally {
        if (isMounted) {
          setLoadingTickets(false);
        }
      }
    };

    loadTickets();

    return () => {
      isMounted = false;
    };
  }, [
    initialContact,
    isAdmin,
    isContactTicketsView,
    modalOpen,
    selectedTicketQueueFilterId,
    serializedTagIds,
    serializedTicketQueueFilterIds,
    serializedTicketWhatsappFilterIds,
    withoutTags,
  ]);

  useEffect(() => {
    if (!modalOpen || activeView !== "createTicket" || isCompactCreateTicketView) {
      setLoadingCreateContacts(false);
      return;
    }

    if (trimmedCreateContactSearch.length < MIN_SEARCH_LENGTH) {
      setLoadingCreateContacts(false);
      setCreateContactResults([]);
      return;
    }

    let isMounted = true;
    setLoadingCreateContacts(true);

    const delayDebounceFn = setTimeout(async () => {
      try {
        const { data } = await api.get("/contacts", {
          params: {
            searchParam: trimmedCreateContactSearch,
            pageNumber: "1",
          },
        });

        if (isMounted) {
          setCreateContactResults(
            Array.isArray(data?.contacts)
              ? data.contacts.filter((contact) => !contact?.isGroup)
              : []
          );
        }
      } catch (err) {
        if (isMounted) {
          setCreateContactResults([]);
          toastError(err);
        }
      } finally {
        if (isMounted) {
          setLoadingCreateContacts(false);
        }
      }
    }, 350);

    return () => {
      isMounted = false;
      clearTimeout(delayDebounceFn);
    };
  }, [activeView, isCompactCreateTicketView, modalOpen, trimmedCreateContactSearch]);

  const handleClose = (selectedTicket) => {
    setPeekDialogOpen(false);
    setPeekTicketId(null);
    onClose(selectedTicket);
  };

  const handleOpenPeekDialog = (event, ticketId) => {
    event.preventDefault();
    event.stopPropagation();
    setPeekTicketId(ticketId);
    setPeekDialogOpen(true);
  };

  const handleClosePeekDialog = () => {
    setPeekDialogOpen(false);
    setPeekTicketId(null);
  };

  const handleOpenContactView = () => {
    setTagsAnchorEl(null);
    setCreateQueueAnchorEl(null);
    setConnectionsAnchorEl(null);
    setTagSearchTerm("");
    setActiveView("contact");
  };

  const handleOpenCreateTicketView = () => {
    setTagsAnchorEl(null);
    setCreateQueueAnchorEl(null);
    setConnectionsAnchorEl(null);
    setTagSearchTerm("");
    setCreateContactSearchTerm((currentValue) => {
      if (currentValue) {
        return currentValue;
      }

      return trimmedSearch || initialContact?.number || initialContact?.name || "";
    });
    setActiveView("createTicket");
  };

  const handleBackToPicker = () => {
    setTagsAnchorEl(null);
    setCreateQueueAnchorEl(null);
    setConnectionsAnchorEl(null);
    setTicketQueueAnchorEl(null);
    setTicketConnectionsAnchorEl(null);
    setTagSearchTerm("");
    setActiveView("picker");
  };

  const handleOpenTagsPopover = (event) => {
    setTagsAnchorEl(event.currentTarget);
  };

  const handleCloseTagsPopover = () => {
    setTagsAnchorEl(null);
  };

  const handleOpenCreateQueuePopover = (event) => {
    setConnectionsAnchorEl(null);
    setCreateQueueAnchorEl(event.currentTarget);
  };

  const handleCloseCreateQueuePopover = () => {
    setCreateQueueAnchorEl(null);
  };

  const handleSelectCreateQueue = (queueId) => {
    setSelectedCreateQueueId(queueId);
    setCreateQueueAnchorEl(null);
  };

  const handleOpenTicketQueuePopover = (event) => {
    setTicketConnectionsAnchorEl(null);
    setTicketQueueAnchorEl(event.currentTarget);
  };

  const handleCloseTicketQueuePopover = () => {
    setTicketQueueAnchorEl(null);
  };

  const handleSelectTicketQueueFilter = (queueId) => {
    setSelectedTicketQueueFilterId(queueId);
    setTicketQueueAnchorEl(null);
  };

  const handleOpenConnectionsPopover = (event) => {
    setCreateQueueAnchorEl(null);
    setConnectionsAnchorEl(event.currentTarget);
  };

  const handleCloseConnectionsPopover = () => {
    setConnectionsAnchorEl(null);
  };

  const handleSelectConnection = (connectionId) => {
    setSelectedWhatsappId(String(connectionId));
    setConnectionsAnchorEl(null);
  };

  const handleOpenTicketConnectionsPopover = (event) => {
    setTicketQueueAnchorEl(null);
    setTicketConnectionsAnchorEl(event.currentTarget);
  };

  const handleCloseTicketConnectionsPopover = () => {
    setTicketConnectionsAnchorEl(null);
  };

  const handleSelectTicketConnectionFilter = (connectionId) => {
    setSelectedTicketWhatsappFilterId(connectionId ? String(connectionId) : "");
    setTicketConnectionsAnchorEl(null);
  };

  const handleClearTagFilters = () => {
    setSelectedTagIds([]);
    setWithoutTags(false);
  };

  const handleToggleNoTags = () => {
    setWithoutTags((currentValue) => {
      const nextValue = !currentValue;

      if (nextValue) {
        setSelectedTagIds([]);
      }

      return nextValue;
    });
  };

  const handleToggleTag = (tagId) => {
    setWithoutTags(false);
    setSelectedTagIds((currentValue) => {
      if (currentValue.includes(tagId)) {
        return currentValue.filter((item) => item !== tagId);
      }

      return [...currentValue, tagId];
    });
  };

  const filteredTags = useMemo(() => {
    if (!trimmedTagSearch) {
      return tags;
    }

    return tags.filter((tag) =>
      String(tag.name || "").toLowerCase().includes(trimmedTagSearch)
    );
  }, [tags, trimmedTagSearch]);

  const filteredCreateQueues = useMemo(() => {
    if (!trimmedCreateQueueSearch) {
      return availableQueues;
    }

    return availableQueues.filter((queue) =>
      String(queue.name || "").toLowerCase().includes(trimmedCreateQueueSearch)
    );
  }, [availableQueues, trimmedCreateQueueSearch]);

  const filteredTicketQueues = useMemo(() => {
    if (!trimmedTicketQueueSearch) {
      return availableQueues;
    }

    return availableQueues.filter((queue) =>
      String(queue.name || "").toLowerCase().includes(trimmedTicketQueueSearch)
    );
  }, [availableQueues, trimmedTicketQueueSearch]);

  const selectedCreateQueue = useMemo(() => {
    if (selectedCreateQueueId === undefined || selectedCreateQueueId === null) {
      return null;
    }

    return (
      availableQueues.find((queue) => queue.id === selectedCreateQueueId) || null
    );
  }, [availableQueues, selectedCreateQueueId]);

  const selectedConnection = useMemo(
    () =>
      connections.find(
        (connection) => String(connection.id) === String(selectedWhatsappId)
      ) || null,
    [connections, selectedWhatsappId]
  );

  const selectedTicketQueueFilter = useMemo(() => {
    if (selectedTicketQueueFilterId === undefined) {
      return null;
    }

    return (
      availableQueues.find((queue) => queue.id === selectedTicketQueueFilterId) ||
      null
    );
  }, [availableQueues, selectedTicketQueueFilterId]);

  const selectedTicketConnectionFilter = useMemo(
    () =>
      connections.find(
        (connection) =>
          String(connection.id) === String(selectedTicketWhatsappFilterId)
      ) || null,
    [connections, selectedTicketWhatsappFilterId]
  );

  const canCreateTicket = Boolean(
    selectedCreateContact &&
      selectedWhatsappId &&
      selectedCreateQueueId !== undefined &&
      !creatingTicket
  );

  const canSubmitNewContactTicket = Boolean(
    selectedWhatsappId &&
      selectedCreateQueueId !== undefined &&
      !creatingTicket
  );

  const selectedTagCount = selectedTagIds.length + (withoutTags ? 1 : 0);

  const statusSections = useMemo(
    () => [
      {
        key: "open",
        label: i18n.t("ticketsList.assignedHeader"),
        tickets: ticketsByStatus.open,
      },
      {
        key: "pending",
        label: i18n.t("ticketsList.pendingHeader"),
        tickets: ticketsByStatus.pending,
      },
      {
        key: "closed",
        label: i18n.t("tickets.tabs.closed.title"),
        tickets: ticketsByStatus.closed,
      },
    ],
    [ticketsByStatus]
  );

  const hasAnyResults = statusSections.some(
    (section) => Array.isArray(section.tickets) && section.tickets.length > 0
  );

  const visibleSections = statusSections.filter(
    (section) => Array.isArray(section.tickets) && section.tickets.length > 0
  );

  const handleTicketClick = (ticket) => {
    if (isContactTicketsView) {
      handleClose(ticket);
      return;
    }

    if (ticket?.status === "closed" && ticket?.hasOpenTicketForContact) {
      toast.warning(i18n.t("backendErrors.ERR_OTHER_OPEN_TICKET"));
      handleClose();
      return;
    }

    handleClose(ticket);
  };

  const handleCreateTicket = async () => {
    if (!selectedCreateContact || !selectedWhatsappId || selectedCreateQueueId === undefined) {
      return;
    }

    setCreatingTicket(true);

    try {
      const { data } = await api.post("/tickets", {
        contactId: selectedCreateContact.id,
        queueId: selectedCreateQueueId,
        whatsappId: selectedWhatsappId,
        userId: user.id,
        status: "open",
      });

      if (data?.alreadyExists) {
        toast.warning(i18n.t("backendErrors.ERR_OTHER_OPEN_TICKET"));
        return;
      }

      handleClose(data);
    } catch (err) {
      toastError(err);
    } finally {
      setCreatingTicket(false);
    }
  };

  const handleContactSave = async (contactData) => {
    if (!contactData?.id || !selectedWhatsappId || selectedCreateQueueId === undefined) {
      return false;
    }

    setCreatingTicket(true);

    try {
      const { data } = await api.post("/tickets", {
        contactId: contactData.id,
        queueId: selectedCreateQueueId,
        whatsappId: selectedWhatsappId,
        userId: user.id,
        status: "open",
      });

      if (data?.alreadyExists) {
        toast.warning(i18n.t("backendErrors.ERR_OTHER_OPEN_TICKET"));
        return false;
      }

      handleClose(data);
      return false;
    } catch (err) {
      toastError(err);
      return false;
    } finally {
      setCreatingTicket(false);
    }
  };

  const createQueueTriggerLabel =
    selectedCreateQueueId === undefined
      ? i18n.t("newTicketModal.queuePlaceholder")
      : selectedCreateQueueId === null
      ? i18n.t("newTicketModal.noQueueOption")
      : selectedCreateQueue?.name || i18n.t("newTicketModal.queuePlaceholder");

  const ticketQueueFilterTriggerLabel =
    selectedTicketQueueFilter?.name || i18n.t("newTicketModal.queuePopoverTitle");

  const ticketConnectionFilterTriggerLabel =
    selectedTicketConnectionFilter?.name ||
    i18n.t("newTicketModal.connectionPopoverTitle");

  const renderQueueSelectControl = (stacked = false) => (
    <div
      className={`${classes.createQueueControl} ${
        stacked ? classes.createStackedSelectControl : ""
      }`}
    >
      <ButtonBase
        className={`${classes.tagsTrigger} ${classes.createSelectTrigger}`}
        onClick={handleOpenCreateQueuePopover}
      >
        <div
          className={`${classes.tagsTriggerContent} ${classes.createSelectTriggerContent}`}
        >
          <Typography
            className={`${classes.tagsTriggerLabel} ${classes.createSelectTriggerLabel} ${
              selectedCreateQueueId === undefined ? classes.selectPlaceholder : ""
            }`}
          >
            {createQueueTriggerLabel}
          </Typography>
        </div>
        <ChevronDown className={classes.tagsTriggerIcon} />
      </ButtonBase>

      <Popover
        open={createQueuePopoverOpen}
        anchorEl={createQueueAnchorEl}
        onClose={handleCloseCreateQueuePopover}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        className={classes.tagsPopover}
      >
        <div className={classes.tagsPopoverHeader}>
          <Typography className={classes.tagsPopoverTitle}>
            {i18n.t("newTicketModal.queuePopoverTitle")}
          </Typography>
          <IconButton
            size="small"
            className={classes.tagsPopoverClose}
            onClick={handleCloseCreateQueuePopover}
          >
            <X size={18} />
          </IconButton>
        </div>

        <div className={classes.tagsPopoverBody}>
          <div className={classes.tagsSearchField}>
            <Search className={classes.searchIcon} />
            <InputBase
              value={createQueueSearchTerm}
              onChange={(event) => setCreateQueueSearchTerm(event.target.value)}
              placeholder={i18n.t("newTicketModal.queueSearchPlaceholder")}
              className={classes.tagsSearchInput}
            />
          </div>

          <div className={classes.tagsStatusRow}>
            <Typography className={classes.tagsStatusText}>
              {selectedCreateQueueId !== undefined
                ? `1 ${i18n.t("newTicketModal.singleSelected")}`
                : i18n.t("newTicketModal.showingAll")}
            </Typography>
          </div>

          <Divider className={classes.tagsDivider} />

          <div className={classes.tagsOptionsScrollArea}>
            <ButtonBase
              className={`${classes.tagOption} ${
                selectedCreateQueueId === null ? classes.tagOptionSelected : ""
              }`}
              onClick={() => handleSelectCreateQueue(null)}
            >
              <Typography className={classes.tagOptionLabel}>
                {i18n.t("newTicketModal.noQueueOption")}
              </Typography>
              {selectedCreateQueueId === null && (
                <Check className={classes.tagOptionCheck} />
              )}
            </ButtonBase>

            {filteredCreateQueues.length === 0 ? (
              <Typography className={classes.tagEmptyState}>
                {i18n.t("newTicketModal.noQueuesFound")}
              </Typography>
            ) : (
              filteredCreateQueues.map((queue) => {
                const isSelected = selectedCreateQueueId === queue.id;
                const queueOptionBackground = getColorWithOpacity(
                  queue.color,
                  isSelected ? 0.26 : 0.16,
                  isSelected
                    ? "rgba(59, 130, 246, 0.18)"
                    : "rgba(59, 130, 246, 0.08)"
                );

                return (
                  <ButtonBase
                    key={queue.id}
                    className={`${classes.tagOption} ${
                      isSelected ? classes.tagOptionSelected : ""
                    }`}
                    onClick={() => handleSelectCreateQueue(queue.id)}
                    style={{ backgroundColor: queueOptionBackground }}
                  >
                    <Typography className={classes.tagOptionLabel}>
                      {queue.name}
                    </Typography>
                    {isSelected && (
                      <Check className={classes.tagOptionCheck} />
                    )}
                  </ButtonBase>
                );
              })
            )}
          </div>
        </div>
      </Popover>
    </div>
  );

  const renderConnectionSelectControl = (stacked = false) => (
    <div
      className={`${classes.createConnectionControl} ${
        stacked ? classes.createStackedSelectControl : ""
      }`}
    >
      <ButtonBase
        className={`${classes.tagsTrigger} ${classes.createSelectTrigger}`}
        onClick={handleOpenConnectionsPopover}
      >
        <div
          className={`${classes.tagsTriggerContent} ${classes.createSelectTriggerContent}`}
        >
          <Typography
            className={`${classes.tagsTriggerLabel} ${classes.createSelectTriggerLabel} ${
              !selectedConnection ? classes.selectPlaceholder : ""
            }`}
          >
            {selectedConnection?.name ||
              i18n.t("newTicketModal.connectionPlaceholder")}
          </Typography>
        </div>
        <ChevronDown className={classes.tagsTriggerIcon} />
      </ButtonBase>

      <Popover
        open={connectionsPopoverOpen}
        anchorEl={connectionsAnchorEl}
        onClose={handleCloseConnectionsPopover}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        className={classes.tagsPopover}
      >
        <div className={classes.tagsPopoverHeader}>
          <Typography className={classes.tagsPopoverTitle}>
            {i18n.t("newTicketModal.connectionPopoverTitle")}
          </Typography>
          <IconButton
            size="small"
            className={classes.tagsPopoverClose}
            onClick={handleCloseConnectionsPopover}
          >
            <X size={18} />
          </IconButton>
        </div>

        <div className={classes.tagsPopoverBody}>
          <Divider className={classes.tagsDivider} />

          <div className={classes.tagsOptionsScrollArea}>
            {loadingConnections ? (
              <Typography className={classes.tagEmptyState}>
                {i18n.t("newTicketModal.loadingConnections")}
              </Typography>
            ) : connections.length === 0 ? (
              <Typography className={classes.tagEmptyState}>
                {i18n.t("newTicketModal.noConnectionsAvailable")}
              </Typography>
            ) : (
              connections.map((connection) => {
                const isSelected =
                  String(connection.id) === String(selectedWhatsappId);

                return (
                  <ButtonBase
                    key={connection.id}
                    className={`${classes.tagOption} ${
                      isSelected ? classes.tagOptionSelected : ""
                    }`}
                    onClick={() => handleSelectConnection(connection.id)}
                  >
                    <Typography className={classes.tagOptionLabel}>
                      {connection.name}
                    </Typography>
                    {isSelected && (
                      <Check className={classes.tagOptionCheck} />
                    )}
                  </ButtonBase>
                );
              })
            )}
          </div>
        </div>
      </Popover>
    </div>
  );

  const renderTicketQueueFilterControl = () => (
    <div className={classes.createQueueControl}>
      <ButtonBase
        className={`${classes.tagsTrigger} ${classes.createSelectTrigger}`}
        onClick={handleOpenTicketQueuePopover}
      >
        <div
          className={`${classes.tagsTriggerContent} ${classes.createSelectTriggerContent}`}
        >
          <Typography
            className={`${classes.tagsTriggerLabel} ${classes.createSelectTriggerLabel} ${
              !selectedTicketQueueFilter ? classes.selectPlaceholder : ""
            }`}
          >
            {ticketQueueFilterTriggerLabel}
          </Typography>
        </div>
        <ChevronDown className={classes.tagsTriggerIcon} />
      </ButtonBase>

      <Popover
        open={ticketQueuePopoverOpen}
        anchorEl={ticketQueueAnchorEl}
        onClose={handleCloseTicketQueuePopover}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        className={classes.tagsPopover}
      >
        <div className={classes.tagsPopoverHeader}>
          <Typography className={classes.tagsPopoverTitle}>
            {i18n.t("newTicketModal.queuePopoverTitle")}
          </Typography>
          <IconButton
            size="small"
            className={classes.tagsPopoverClose}
            onClick={handleCloseTicketQueuePopover}
          >
            <X size={18} />
          </IconButton>
        </div>

        <div className={classes.tagsPopoverBody}>
          <div className={classes.tagsSearchField}>
            <Search className={classes.searchIcon} />
            <InputBase
              value={ticketQueueSearchTerm}
              onChange={(event) => setTicketQueueSearchTerm(event.target.value)}
              placeholder={i18n.t("newTicketModal.queueSearchPlaceholder")}
              className={classes.tagsSearchInput}
            />
          </div>

          <div className={classes.tagsStatusRow}>
            <Typography className={classes.tagsStatusText}>
              {selectedTicketQueueFilter
                ? `1 ${i18n.t("newTicketModal.singleSelected")}`
                : i18n.t("newTicketModal.showingAll")}
            </Typography>
          </div>

          <Divider className={classes.tagsDivider} />

          <div className={classes.tagsOptionsScrollArea}>
            <ButtonBase
              className={`${classes.tagOption} ${
                selectedTicketQueueFilterId === undefined
                  ? classes.tagOptionSelected
                  : ""
              }`}
              onClick={() => handleSelectTicketQueueFilter(undefined)}
            >
              <Typography className={classes.tagOptionLabel}>
                {i18n.t("newTicketModal.allQueuesOption")}
              </Typography>
              {selectedTicketQueueFilterId === undefined && (
                <Check className={classes.tagOptionCheck} />
              )}
            </ButtonBase>

            {filteredTicketQueues.length === 0 ? (
              <Typography className={classes.tagEmptyState}>
                {i18n.t("newTicketModal.noQueuesFound")}
              </Typography>
            ) : (
              filteredTicketQueues.map((queue) => {
                const isSelected = selectedTicketQueueFilterId === queue.id;
                const queueOptionBackground = getColorWithOpacity(
                  queue.color,
                  isSelected ? 0.26 : 0.16,
                  isSelected
                    ? "rgba(59, 130, 246, 0.18)"
                    : "rgba(59, 130, 246, 0.08)"
                );

                return (
                  <ButtonBase
                    key={queue.id}
                    className={`${classes.tagOption} ${
                      isSelected ? classes.tagOptionSelected : ""
                    }`}
                    onClick={() => handleSelectTicketQueueFilter(queue.id)}
                    style={{ backgroundColor: queueOptionBackground }}
                  >
                    <Typography className={classes.tagOptionLabel}>
                      {queue.name}
                    </Typography>
                    {isSelected && (
                      <Check className={classes.tagOptionCheck} />
                    )}
                  </ButtonBase>
                );
              })
            )}
          </div>
        </div>
      </Popover>
    </div>
  );

  const renderTicketConnectionFilterControl = () => (
    <div className={classes.createConnectionControl}>
      <ButtonBase
        className={`${classes.tagsTrigger} ${classes.createSelectTrigger}`}
        onClick={handleOpenTicketConnectionsPopover}
      >
        <div
          className={`${classes.tagsTriggerContent} ${classes.createSelectTriggerContent}`}
        >
          <Typography
            className={`${classes.tagsTriggerLabel} ${classes.createSelectTriggerLabel} ${
              !selectedTicketConnectionFilter ? classes.selectPlaceholder : ""
            }`}
          >
            {ticketConnectionFilterTriggerLabel}
          </Typography>
        </div>
        <ChevronDown className={classes.tagsTriggerIcon} />
      </ButtonBase>

      <Popover
        open={ticketConnectionsPopoverOpen}
        anchorEl={ticketConnectionsAnchorEl}
        onClose={handleCloseTicketConnectionsPopover}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        className={classes.tagsPopover}
      >
        <div className={classes.tagsPopoverHeader}>
          <Typography className={classes.tagsPopoverTitle}>
            {i18n.t("newTicketModal.connectionPopoverTitle")}
          </Typography>
          <IconButton
            size="small"
            className={classes.tagsPopoverClose}
            onClick={handleCloseTicketConnectionsPopover}
          >
            <X size={18} />
          </IconButton>
        </div>

        <div className={classes.tagsPopoverBody}>
          <Divider className={classes.tagsDivider} />

          <div className={classes.tagsOptionsScrollArea}>
            <ButtonBase
              className={`${classes.tagOption} ${
                !selectedTicketWhatsappFilterId ? classes.tagOptionSelected : ""
              }`}
              onClick={() => handleSelectTicketConnectionFilter("")}
            >
              <Typography className={classes.tagOptionLabel}>
                {i18n.t("newTicketModal.allConnectionsOption")}
              </Typography>
              {!selectedTicketWhatsappFilterId && (
                <Check className={classes.tagOptionCheck} />
              )}
            </ButtonBase>

            {loadingConnections ? (
              <Typography className={classes.tagEmptyState}>
                {i18n.t("newTicketModal.loadingConnections")}
              </Typography>
            ) : connections.length === 0 ? (
              <Typography className={classes.tagEmptyState}>
                {i18n.t("newTicketModal.noConnectionsAvailable")}
              </Typography>
            ) : (
              connections.map((connection) => {
                const isSelected =
                  String(connection.id) === String(selectedTicketWhatsappFilterId);

                return (
                  <ButtonBase
                    key={connection.id}
                    className={`${classes.tagOption} ${
                      isSelected ? classes.tagOptionSelected : ""
                    }`}
                    onClick={() => handleSelectTicketConnectionFilter(connection.id)}
                  >
                    <Typography className={classes.tagOptionLabel}>
                      {connection.name}
                    </Typography>
                    {isSelected && (
                      <Check className={classes.tagOptionCheck} />
                    )}
                  </ButtonBase>
                );
              })
            )}
          </div>
        </div>
      </Popover>
    </div>
  );

  const renderTagsFilterControl = () => (
    <div className={classes.tagsControl}>
      <ButtonBase
        className={classes.tagsTrigger}
        onClick={handleOpenTagsPopover}
      >
        <div className={classes.tagsTriggerContent}>
          <Typography className={classes.tagsTriggerLabel}>
            {i18n.t("newTicketModal.tagsPlaceholder")}
          </Typography>
          {selectedTagCount > 0 && (
            <span className={classes.tagsCountBadge}>{selectedTagCount}</span>
          )}
        </div>
        <ChevronDown className={classes.tagsTriggerIcon} />
      </ButtonBase>

      <Popover
        open={tagsPopoverOpen}
        anchorEl={tagsAnchorEl}
        onClose={handleCloseTagsPopover}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        className={classes.tagsPopover}
      >
        <div className={classes.tagsPopoverHeader}>
          <Typography className={classes.tagsPopoverTitle}>
            {i18n.t("newTicketModal.tagsPopoverTitle")}
          </Typography>
          <IconButton
            size="small"
            className={classes.tagsPopoverClose}
            onClick={handleCloseTagsPopover}
          >
            <X size={18} />
          </IconButton>
        </div>

        <div className={classes.tagsPopoverBody}>
          <div className={classes.tagsSearchField}>
            <Search className={classes.searchIcon} />
            <InputBase
              value={tagSearchTerm}
              onChange={(event) => setTagSearchTerm(event.target.value)}
              placeholder={i18n.t("newTicketModal.tagsSearchPlaceholder")}
              className={classes.tagsSearchInput}
            />
          </div>

          <div className={classes.tagsStatusRow}>
            <Typography className={classes.tagsStatusText}>
              {selectedTagCount > 0
                ? `${selectedTagCount} ${i18n.t(
                    selectedTagCount === 1
                      ? "newTicketModal.singleSelected"
                      : "newTicketModal.multipleSelected"
                  )}`
                : i18n.t("newTicketModal.showingAll")}
            </Typography>

            {selectedTagCount > 0 && (
              <button
                type="button"
                className={classes.tagsClearButton}
                onClick={handleClearTagFilters}
              >
                {i18n.t("newTicketModal.clear")}
              </button>
            )}
          </div>

          <Divider className={classes.tagsDivider} />

          <div className={classes.tagsOptionsScrollArea}>
            <ButtonBase
              className={`${classes.tagOption} ${
                withoutTags ? classes.tagOptionSelected : ""
              }`}
              onClick={handleToggleNoTags}
            >
              <Typography className={classes.tagOptionLabel}>
                {i18n.t("newTicketModal.noTagsOption")}
              </Typography>
              {withoutTags && <Check className={classes.tagOptionCheck} />}
            </ButtonBase>

            {loadingTags ? (
              <Typography className={classes.tagEmptyState}>
                {i18n.t("newTicketModal.loadingTags")}
              </Typography>
            ) : filteredTags.length === 0 ? (
              <Typography className={classes.tagEmptyState}>
                {i18n.t("newTicketModal.noTagsFound")}
              </Typography>
            ) : (
              filteredTags.map((tag) => {
                const isSelected = selectedTagIds.includes(tag.id);

                return (
                  <ButtonBase
                    key={tag.id}
                    className={`${classes.tagOption} ${
                      isSelected ? classes.tagOptionSelected : ""
                    }`}
                    onClick={() => handleToggleTag(tag.id)}
                  >
                    <span
                      className={classes.tagOptionChip}
                      style={{ backgroundColor: tag.color || "#4b5563" }}
                    >
                      {tag.name}
                    </span>
                    {isSelected && (
                      <Check className={classes.tagOptionCheck} />
                    )}
                  </ButtonBase>
                );
              })
            )}
          </div>
        </div>
      </Popover>
    </div>
  );

  const renderTicketsList = (emptyMessage) => (
    <div className={classes.listContainer}>
      {loadingTickets ? (
        <div className={classes.emptyState}>
          <CircularProgress size={28} color="primary" />
        </div>
      ) : !hasAnyResults ? (
        <div className={classes.emptyState}>
          <Typography>{emptyMessage}</Typography>
        </div>
      ) : (
        <List disablePadding>
          {visibleSections.map((section, sectionIndex) => {
            return (
              <React.Fragment key={section.key}>
                {sectionIndex > 0 && <Divider component="li" />}
                <Typography className={classes.sectionHeader}>
                  {section.label}
                </Typography>

                {section.tickets.map((ticket, index) => (
                  <React.Fragment key={ticket.id}>
                    <ListItem
                      button
                      className={classes.ticketItem}
                      onClick={() => handleTicketClick(ticket)}
                    >
                      <ListItemAvatar>
                        <Avatar
                          src={ticket?.contact?.profilePicUrl}
                          className={classes.ticketAvatar}
                        >
                          {(ticket?.contact?.name || ticket?.contact?.number || "?")
                            .charAt(0)
                            .toUpperCase()}
                        </Avatar>
                      </ListItemAvatar>

                      <ListItemText
                        primary={
                          <div className={classes.ticketPrimaryRow}>
                            <Typography className={classes.ticketName}>
                              {ticket?.contact?.name ||
                                ticket?.contact?.number ||
                                "Contato"}
                            </Typography>
                            {Array.isArray(ticket?.tags) && ticket.tags.length > 0 && (
                              <div className={classes.ticketTagsRow}>
                                {ticket.tags.map((tag) => (
                                  <span
                                    key={`${ticket.id}-${tag.id}`}
                                    className={classes.ticketTagChip}
                                    style={{
                                      backgroundColor:
                                        tag.color ||
                                        (ticket?.status === "closed"
                                          ? "#475569"
                                          : "#3b82f6"),
                                    }}
                                  >
                                    {tag.name}
                                  </span>
                                ))}
                              </div>
                            )}
                          </div>
                        }
                        secondary={
                          <Typography className={classes.ticketNumber}>
                            {formatPhoneNumber(ticket?.contact?.number || "")}
                          </Typography>
                        }
                      />

                      <div className={classes.ticketActions}>
                        {isContactTicketsView && (
                          <Tooltip title={i18n.t("ticketsListItem.tooltip.peek")}>
                            <IconButton
                              size="small"
                              className={classes.ticketPeekButton}
                              onClick={(event) => handleOpenPeekDialog(event, ticket.id)}
                            >
                              <Eye className={classes.ticketPeekIcon} strokeWidth={1.5} />
                            </IconButton>
                          </Tooltip>
                        )}
                        <KeyboardArrowRightIcon className={classes.ticketArrow} />
                      </div>
                    </ListItem>

                    {index < section.tickets.length - 1 && (
                      <Divider component="li" />
                    )}
                  </React.Fragment>
                ))}
              </React.Fragment>
            );
          })}
        </List>
      )}
    </div>
  );

  const dialogTitle =
    activeView === "contact"
      ? i18n.t("contactModal.title.add")
      : isContactTicketsView
      ? i18n.t("newTicketModal.ticketTitle")
      : isCreateTicketView || isResolvingStartConversationView
      ? i18n.t("newTicketModal.createTicket")
      : i18n.t("newTicketModal.title");

  return (
    <>
      <Dialog
        open={modalOpen}
        onClose={() => handleClose()}
        fullWidth
        maxWidth={false}
        className={classes.dialog}
        PaperProps={{
          className: isCompactCreateTicketView ? classes.compactDialogPaper : undefined,
        }}
      >
        <DialogTitle disableTypography className={classes.dialogTitle}>
          <div className={classes.dialogHeaderLead}>
            {activeView !== "picker" &&
              !isCompactCreateTicketView &&
              !isContactTicketsView && (
              <IconButton
                size="small"
                className={classes.backButton}
                onClick={handleBackToPicker}
              >
                <ArrowLeft />
              </IconButton>
            )}
            <Typography className={classes.titleText}>{dialogTitle}</Typography>
          </div>

          <IconButton
            size="small"
            className={classes.closeButton}
            onClick={() => handleClose()}
          >
            <X />
          </IconButton>
        </DialogTitle>

        <DialogContent className={classes.dialogContent}>
          {activeView === "contact" ? (
            <div className={classes.contactView}>
              <div className={classes.createTicketFields}>
                <div className={classes.createTicketPrimaryRow}>
                  {renderQueueSelectControl()}
                  {renderConnectionSelectControl()}
                </div>
              </div>

              <ContactModal
                open={activeView === "contact"}
                embedded
                onClose={handleBackToPicker}
                onSave={handleContactSave}
                submitDisabled={!canSubmitNewContactTicket}
              />
            </div>
          ) : isResolvingStartConversationView ? (
            <div className={classes.createTicketView}>
              <div className={classes.createEmptyState}>
                <CircularProgress size={28} color="primary" />
              </div>
            </div>
          ) : isCreateTicketView ? (
            <div
              className={`${classes.createTicketView} ${
                isCompactCreateTicketView ? classes.compactCreateTicketView : ""
              }`}
            >
              <div className={classes.createTicketFields}>
                <div
                  className={`${classes.createTicketPrimaryRow} ${
                    isCompactCreateTicketView ? classes.createTicketStackedFields : ""
                  }`}
                >
                  {!isCompactCreateTicketView && (
                    <div className={`${classes.searchField} ${classes.createSearchField}`}>
                      <Search className={classes.searchIcon} />
                      <InputBase
                        autoFocus
                        value={createContactSearchTerm}
                        onChange={(event) => {
                          setCreateContactSearchTerm(event.target.value);
                          setSelectedCreateContact(null);
                        }}
                        placeholder={i18n.t("newTicketModal.existingContactPlaceholder")}
                        className={classes.searchInput}
                      />
                    </div>
                  )}

                  {renderQueueSelectControl(isCompactCreateTicketView)}
                  {renderConnectionSelectControl(isCompactCreateTicketView)}
                </div>
              </div>

              {!isCompactCreateTicketView && (
                <div className={classes.createResultsContainer}>
                  {loadingCreateContacts ? (
                    <div className={classes.createEmptyState}>
                      <CircularProgress size={28} color="primary" />
                    </div>
                  ) : trimmedCreateContactSearch.length < MIN_SEARCH_LENGTH ? (
                    <div className={classes.createEmptyState}>
                      <Typography>
                        {i18n.t("newTicketModal.contactSearchHint")}
                      </Typography>
                    </div>
                  ) : createContactResults.length === 0 ? (
                    <div className={classes.createEmptyState}>
                      <Typography>{i18n.t("newTicketModal.emptyState")}</Typography>
                    </div>
                  ) : (
                    <List disablePadding>
                      {createContactResults.map((contact, index) => {
                        const isSelected = selectedCreateContact?.id === contact.id;

                        return (
                          <React.Fragment key={contact.id}>
                            <ListItem
                              button
                              className={`${classes.ticketItem} ${
                                isSelected ? classes.contactResultItemSelected : ""
                              }`}
                              onClick={() => setSelectedCreateContact(contact)}
                            >
                              <ListItemAvatar>
                                <Avatar
                                  src={contact?.profilePicUrl}
                                  className={classes.ticketAvatar}
                                >
                                  {(contact?.name || contact?.number || "?")
                                    .charAt(0)
                                    .toUpperCase()}
                                </Avatar>
                              </ListItemAvatar>

                              <ListItemText
                                primary={
                                  <Typography className={classes.ticketName}>
                                    {contact?.name || contact?.number || "Contato"}
                                  </Typography>
                                }
                                secondary={
                                  <Typography className={classes.ticketNumber}>
                                    {formatPhoneNumber(contact?.number || "")}
                                  </Typography>
                                }
                              />

                              {isSelected ? (
                                <Check className={classes.contactResultCheck} />
                              ) : (
                                <KeyboardArrowRightIcon className={classes.ticketArrow} />
                              )}
                            </ListItem>

                            {index < createContactResults.length - 1 && (
                              <Divider component="li" />
                            )}
                          </React.Fragment>
                        );
                      })}
                    </List>
                  )}
                </div>
              )}

              <div className={classes.createActions}>
                <Button
                  variant="contained"
                  color="primary"
                  className={classes.newContactButton}
                  disabled={!canCreateTicket}
                  onClick={handleCreateTicket}
                  startIcon={
                    creatingTicket ? null : <Plus size={15} strokeWidth={1.75} />
                  }
                >
                  {creatingTicket ? (
                    <CircularProgress size={20} color="inherit" />
                  ) : (
                    i18n.t("newTicketModal.createTicket")
                  )}
                </Button>
              </div>
            </div>
          ) : isContactTicketsView ? (
            <>
              <div className={classes.filtersRow}>
                {renderTagsFilterControl()}
                {renderTicketQueueFilterControl()}
                {renderTicketConnectionFilterControl()}
              </div>

              {renderTicketsList(i18n.t("newTicketModal.emptyTicketsState"))}
            </>
          ) : (
            <>
              <div className={classes.filtersRow}>
                <div className={classes.searchField}>
                  <Search className={classes.searchIcon} />
                  <InputBase
                    autoFocus
                    value={searchTerm}
                    onChange={(event) => setSearchTerm(event.target.value)}
                    placeholder={i18n.t("newTicketModal.searchPlaceholder")}
                    className={classes.searchInput}
                  />
                </div>

                {renderTagsFilterControl()}

                <div className={classes.actionsGroup}>
                  <Button
                    variant="outlined"
                    className={classes.createTicketButton}
                    onClick={handleOpenCreateTicketView}
                    startIcon={<Plus size={15} strokeWidth={1.75} />}
                  >
                    {i18n.t("newTicketModal.createTicket")}
                  </Button>

                  <Button
                    variant="contained"
                    color="primary"
                    className={classes.newContactButton}
                    onClick={handleOpenContactView}
                    startIcon={<UserPlus size={15} strokeWidth={1.75} />}
                  >
                    {i18n.t("newTicketModal.newContact")}
                  </Button>
                </div>
              </div>

              {renderTicketsList(i18n.t("newTicketModal.contactSearchHint"))}
            </>
          )}
        </DialogContent>
      </Dialog>

      {peekDialogOpen && peekTicketId && (
        <TicketMessagesDialog
          open={peekDialogOpen}
          handleClose={handleClosePeekDialog}
          ticketId={peekTicketId}
        />
      )}
    </>
  );
};

export default NewTicketModal;
